import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import authService from '../services/auth.service';
import { toast } from 'react-toastify';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    password: '',
    passwordConfirmation: '',
  });
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);

  useEffect(() => {
    const tokenParam = searchParams.get('token');
    const emailParam = searchParams.get('email');

    if (!tokenParam || !emailParam) {
      toast.error('Invalid reset link');
      navigate('/login');
      return;
    }

    setToken(tokenParam);
    setEmail(emailParam);

    // Verify token on load
    const verifyToken = async () => {
      try {
        const result = await authService.verifyResetToken(
          tokenParam,
          emailParam
        );
        if (result.valid) {
          setTokenValid(true);
        } else {
          toast.error(result.message || 'Invalid or expired reset link');
          setTimeout(() => navigate('/login'), 2000);
        }
      } catch (error: any) {
        toast.error('Failed to verify reset link');
        setTimeout(() => navigate('/login'), 2000);
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [searchParams, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.password !== formData.passwordConfirmation) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    try {
      const response = await authService.resetPassword(
        token,
        email,
        formData.password,
        formData.passwordConfirmation
      );
      toast.success(response.message);
      setTimeout(() => navigate('/login'), 2000);
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || 'Failed to reset password'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  if (verifying) {
    return (
      <div className='text-center'>
        <div className='spinner-border text-primary mb-3' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
        <p className='text-muted'>Verifying reset link...</p>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <>
        <h4 className='fw-semibold mb-2 text-danger'>Invalid Reset Link</h4>
        <p className='mb-4 text-muted'>
          The password reset link is invalid or has expired.
        </p>
        <div className='text-center'>
          <Link to='/forgot-password' className='btn btn-primary btn-lg'>
            Request New Link
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
      <h4 className='fw-semibold mb-2'>Reset Password</h4>
      <p className='mb-4 text-muted op-7 fw-normal'>
        Enter your new password below
      </p>

      <form onSubmit={handleSubmit}>
        <div className='mb-3'>
          <label htmlFor='email' className='form-label'>
            Email Address
          </label>
          <input
            type='email'
            className='form-control form-control-lg'
            id='email'
            value={email}
            disabled
          />
        </div>

        <div className='mb-3'>
          <label htmlFor='password' className='form-label'>
            New Password
          </label>
          <div className='input-group'>
            <input
              type={showPassword ? 'text' : 'password'}
              className='form-control form-control-lg'
              id='password'
              name='password'
              placeholder='Enter new password'
              value={formData.password}
              onChange={handleChange}
              required
              minLength={8}
            />
            <button
              className='btn btn-light'
              type='button'
              onClick={() => setShowPassword(!showPassword)}
            >
              <i
                className={`ri-eye-${showPassword ? 'off-' : ''}line align-middle`}
              ></i>
            </button>
          </div>
          <small className='text-muted'>
            Password must be at least 8 characters long
          </small>
        </div>

        <div className='mb-3'>
          <label htmlFor='passwordConfirmation' className='form-label'>
            Confirm New Password
          </label>
          <div className='input-group'>
            <input
              type={showPasswordConfirmation ? 'text' : 'password'}
              className='form-control form-control-lg'
              id='passwordConfirmation'
              name='passwordConfirmation'
              placeholder='Confirm new password'
              value={formData.passwordConfirmation}
              onChange={handleChange}
              required
              minLength={8}
            />
            <button
              className='btn btn-light'
              type='button'
              onClick={() =>
                setShowPasswordConfirmation(!showPasswordConfirmation)
              }
            >
              <i
                className={`ri-eye-${showPasswordConfirmation ? 'off-' : ''}line align-middle`}
              ></i>
            </button>
          </div>
        </div>

        <button
          type='submit'
          className='btn btn-primary btn-lg w-100 mb-3'
          disabled={loading}
        >
          {loading ? (
            <>
              <span
                className='spinner-border spinner-border-sm me-2'
                role='status'
                aria-hidden='true'
              ></span>
              Resetting Password...
            </>
          ) : (
            'Reset Password'
          )}
        </button>
      </form>

      <div className='text-center'>
        <p className='text-muted mt-3'>
          Remember your password?{' '}
          <Link to='/login' className='text-primary fw-semibold'>
            Sign In
          </Link>
        </p>
      </div>
    </>
  );
};

export default ResetPassword;

